import { PAGES, type PageName } from "constants/Pages";
import type { AccountGroupType } from "features/account-groups-form/accountsGroups.model";
import type { AccountType } from "shared/types/accounts";
import type { Post } from "shared/types/post";

export type PostsView = "list" | "table";

const POSTS_ROUTE_SEGMENTS: Record<PageName, string> = {
  [PAGES.DRAFT]: "drafts",
  [PAGES.UPCOMING]: "active",
  [PAGES.ARCHIVE]: "inactive",
  [PAGES.PLANNER]: "active",
  [PAGES.CALENDAR]: "active",
};

export type PostsDeepLinkErrorState = {
  title: string;
  description: string;
};

export function isPostDeepLinkId(value: string | null | undefined): boolean {
  return typeof value === "string" && /^[\da-f]{24}$/i.test(value);
}

export function getConsumablePostsDeepLinkPostId(
  search: string
): string | null {
  const postId = new URLSearchParams(search).get("postId");
  return isPostDeepLinkId(postId) ? postId : null;
}

export function isPostsListPath(pathname: string): boolean {
  return getPostsViewFromPath(pathname) === "list";
}

export function getPostsViewFromPath(pathname: string): PostsView | null {
  const match =
    /^\/dashboard\/schedules\/(list|table)\/(?:drafts|active|inactive)\/?$/.exec(
      pathname
    );
  return (match?.[1] as PostsView | undefined) ?? null;
}

export function isPostsShellPath(pathname: string): boolean {
  return getPostsViewFromPath(pathname) !== null;
}

export function buildPostsRoute(
  page: PageName,
  view: PostsView = "list",
  search = ""
): string {
  const query = search.replace(/^\?/, "");
  const pathname = `/dashboard/schedules/${view}/${POSTS_ROUTE_SEGMENTS[page]}`;
  return `${pathname}${query ? `?${query}` : ""}`;
}

export function buildPostsListRoute(page: PageName, search = ""): string {
  return buildPostsRoute(page, "list", search);
}

export function removePostsDeepLinkParam(search: string): string {
  const searchParams = new URLSearchParams(search);
  searchParams.delete("postId");
  searchParams.delete("boostAgain");
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

export function resolvePostsDeepLinkPage(post: Post): PageName {
  const postType = post.postType?.toLowerCase();

  if (postType === "draft") return PAGES.DRAFT;
  if (["archive", "archived"].includes(postType || "")) {
    return PAGES.ARCHIVE;
  }
  return PAGES.UPCOMING;
}

export function resolvePostsDeepLinkAccountIds(post: Post): string[] {
  const normalizedPost = post.schedule || post;
  return [
    ...(post.accountIds || []),
    ...(normalizedPost.accountIds || []),
    post.accountId,
    normalizedPost.accountId,
  ]
    .filter((accountId): accountId is string => Boolean(accountId))
    .filter((accountId, index, values) => values.indexOf(accountId) === index);
}

export function resolvePostsDeepLinkScopeAccountIds(
  postAccountIds: string[],
  groups: AccountGroupType[],
  accounts: AccountType[]
): string[] {
  const accessibleIds = new Set(accounts.map((account) => String(account._id)));
  const accessiblePostAccountId = postAccountIds.find((accountId) =>
    accessibleIds.has(String(accountId))
  );

  if (!accessiblePostAccountId) return [];

  const group = groups.find((candidate) =>
    candidate.accountIds.some(
      (accountId) => String(accountId) === String(accessiblePostAccountId)
    )
  );

  return group
    ? group.accountIds
        .map(String)
        .filter((accountId) => accessibleIds.has(accountId))
    : [String(accessiblePostAccountId)];
}

export function normalizePostsDeepLinkError(
  error: unknown
): PostsDeepLinkErrorState {
  const typedError = error as {
    status?: number;
    response?: { status?: number };
  };
  const status = typedError.status ?? typedError.response?.status;

  if (status === 403) {
    return {
      title: "Post unavailable",
      description: "You don't have access to this post.",
    };
  }

  if (status === 404) {
    return {
      title: "Post not found",
      description: "This post couldn't be found. It may have been deleted.",
    };
  }

  return {
    title: "Post unavailable",
    description: "This post couldn't be loaded. Please try again.",
  };
}
